import React, { useState, useEffect } from 'react';
import { addObra, syncObraResponsaveis, syncObraUnidades } from '../services/api';
import { Obra, ObraResponsavel, ObraUnidadeInput } from '../services/types';
import { estados, municipios } from '../services/locationData';
import { ObraResponsaveisUnidadesSection } from './ObraResponsaveisUnidadesSection';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    Box,
    Divider,
    Alert,
    Tabs,
    Tab,
    Chip,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';

interface ObraCreateModalProps {
    open: boolean;
    onClose: () => void;
    onObraAdded: () => void;
}

const initialFormData: Partial<Obra> = {
    nome: '',
    endereco: '',
    status: 'Em Andamento',
    orcamento_inicial: '' as any,
    data_inicio: new Date().toISOString().split('T')[0],
    data_termino_prevista: new Date().toISOString().split('T')[0],
    data_limite_correcao: '',
    tipo_de_construcao: 'apartamento',
    quantidade_unidades: '' as any,
    valor_venda_unidade: '' as any,
    cno: '',
    inscricao_municipal: '',
    matricula_imovel: '',
    numero_alvara: '',
    estado: 'RJ',
    municipio: '',
};

const ObraCreateModal: React.FC<ObraCreateModalProps> = ({ open, onClose, onObraAdded }) => {
    const [formData, setFormData] = useState(initialFormData);
    const [municipiosDisponiveis, setMunicipiosDisponiveis] = useState<string[]>([]);
    const [responsaveis, setResponsaveis] = useState<ObraResponsavel[]>([]);
    const [unidades, setUnidades] = useState<ObraUnidadeInput[]>([]);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
        if (open) {
            setFormData(initialFormData);
            setMunicipiosDisponiveis(municipios[initialFormData.estado || 'RJ']);
            setResponsaveis([]);
            setUnidades([]);
            setErrorMsg(null);
            setSaving(false);
            setActiveTab(0);
        }
    }, [open]);

    const handleEstadoChange = (e: React.ChangeEvent<{ value: unknown }>) => {
        const estado = e.target.value as string;
        setFormData((prev) => ({ ...prev, estado, municipio: '' }));
        setMunicipiosDisponiveis(municipios[estado] || []);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name as string]: value }));
    };

    const handleSave = async () => {
        setErrorMsg(null);

        if (!formData.nome?.trim()) {
            setActiveTab(0);
            setErrorMsg('Informe o Nome da Obra.');
            return;
        }
        if (!formData.estado?.trim() || !formData.municipio?.trim()) {
            setActiveTab(0);
            setErrorMsg('Selecione o Estado e o Município da obra.');
            return;
        }
        if (
            formData.data_inicio &&
            formData.data_termino_prevista &&
            formData.data_termino_prevista < formData.data_inicio
        ) {
            setActiveTab(0);
            setErrorMsg('A Data de Término Prevista não pode ser anterior à Data de Início.');
            return;
        }

        for (let i = 0; i < unidades.length; i++) {
            if (!unidades[i].identificacao?.trim()) {
                setActiveTab(2);
                setErrorMsg(`Informe a identificação da Unidade #${i + 1}.`);
                return;
            }
            if ((Number(unidades[i].valor) || 0) < 0) {
                setActiveTab(2);
                setErrorMsg(`O valor da unidade "${unidades[i].identificacao}" não pode ser negativo.`);
                return;
            }
        }

        const orcamentoNum = Number(formData.orcamento_inicial) || 0;
        const somaValoresUnidades = unidades.reduce((acc, u) => acc + (Number(u.valor) || 0), 0);
        const qtdUnidadesNum = unidades.length > 0 ? unidades.length : Number(formData.quantidade_unidades) || 0;
        const valorVendaNum =
            unidades.length > 0
                ? Number((somaValoresUnidades / unidades.length).toFixed(2))
                : Number(formData.valor_venda_unidade) || 0;

        if (orcamentoNum < 0 || qtdUnidadesNum < 0 || valorVendaNum < 0) {
            setErrorMsg('Os valores numéricos não podem ser negativos.');
            return;
        }

        setSaving(true);
        try {
            const dataToSave = {
                ...formData,
                nome: formData.nome.trim(),
                endereco: formData.endereco?.trim() || '',
                orcamento_inicial: orcamentoNum,
                quantidade_unidades: Math.trunc(qtdUnidadesNum),
                valor_venda_unidade: valorVendaNum,
                data_inicio: formData.data_inicio || null,
                data_termino_prevista: formData.data_termino_prevista || null,
                data_limite_correcao: formData.data_limite_correcao || null,
            } as Omit<Obra, 'id'>;

            const createdRows = await addObra(dataToSave);
            const newObraId = createdRows?.[0]?.id;

            if (newObraId) {
                if (responsaveis.length > 0) {
                    await syncObraResponsaveis(
                        newObraId,
                        responsaveis.map((r) => ({
                            investidor_id: r.id,
                            percentual_participacao: r.percentual_participacao,
                        }))
                    );
                }

                if (unidades.length > 0) {
                    await syncObraUnidades(
                        newObraId,
                        {
                            endereco: dataToSave.endereco,
                            municipio: dataToSave.municipio,
                            estado: dataToSave.estado,
                        },
                        unidades
                    );
                }
            }

            onObraAdded();
            onClose();
        } catch (error: any) {
            console.error('Erro ao adicionar obra:', error);
            setErrorMsg(error?.message || 'Erro ao adicionar obra. Verifique os dados informados.');
        } finally {
            setSaving(false);
        }
    };

    const qtdUnidadesBadge = unidades.length > 0 ? unidades.length : Number(formData.quantidade_unidades) || 0;

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="lg"
            fullScreen={isFullScreen}
            PaperProps={{
                sx: {
                    maxHeight: isFullScreen ? '100vh' : '90vh',
                    borderRadius: isFullScreen ? 0 : 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    px: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                    Adicionar Nova Obra
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={isFullScreen ? 'Restaurar tamanho da janela' : 'Expandir para tela cheia'}>
                        <IconButton size="small" onClick={() => setIsFullScreen((prev) => !prev)}>
                            {isFullScreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Fechar">
                        <IconButton size="small" onClick={onClose} disabled={saving}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>

            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2, bgcolor: 'action.hover' }}>
                <Tabs
                    value={activeTab}
                    onChange={(_e, newVal) => setActiveTab(newVal)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ minHeight: 44 }}
                >
                    <Tab
                        icon={<InfoOutlinedIcon fontSize="small" />}
                        iconPosition="start"
                        label="Dados Gerais, Prazos e Documentos"
                        sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}
                    />
                    <Tab
                        icon={<PeopleOutlineIcon fontSize="small" />}
                        iconPosition="start"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>Responsáveis</span>
                                {responsaveis.length > 0 && (
                                    <Chip label={responsaveis.length} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                            </Box>
                        }
                        sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}
                    />
                    <Tab
                        icon={<HomeWorkOutlinedIcon fontSize="small" />}
                        iconPosition="start"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>Unidades, Áreas e Valores</span>
                                {qtdUnidadesBadge > 0 && (
                                    <Chip label={qtdUnidadesBadge} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                            </Box>
                        }
                        sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}
                    />
                </Tabs>
            </Box>

            <DialogContent sx={{ p: 2.5 }}>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {errorMsg && (
                        <Alert severity="error" onClose={() => setErrorMsg(null)}>
                            {errorMsg}
                        </Alert>
                    )}

                    {/* ABA 0: DADOS GERAIS, PRAZOS E DOCUMENTAÇÃO */}
                    {activeTab === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Divider textAlign="left">
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                    INFORMAÇÕES PRINCIPAIS E LOCALIZAÇÃO
                                </Typography>
                            </Divider>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1.2fr 1.4fr 0.8fr' },
                                    gap: 1.5,
                                }}
                            >
                                <TextField
                                    size="small"
                                    name="nome"
                                    label="Nome da Obra"
                                    value={formData.nome}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    name="endereco"
                                    label="Endereço"
                                    value={formData.endereco}
                                    onChange={handleChange}
                                    fullWidth
                                />
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select name="status" value={formData.status} label="Status" onChange={handleChange as any}>
                                        <MenuItem value="Em Andamento">Em Andamento</MenuItem>
                                        <MenuItem value="Planejada">Planejada</MenuItem>
                                        <MenuItem value="Pausada">Pausada</MenuItem>
                                        <MenuItem value="Concluída">Concluída</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' },
                                    gap: 1.5,
                                }}
                            >
                                <FormControl size="small" fullWidth required>
                                    <InputLabel>Estado</InputLabel>
                                    <Select name="estado" value={formData.estado} label="Estado" onChange={handleEstadoChange as any}>
                                        {estados.map((e) => (
                                            <MenuItem key={e.sigla} value={e.sigla}>
                                                {e.nome}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" fullWidth required>
                                    <InputLabel>Município</InputLabel>
                                    <Select
                                        name="municipio"
                                        value={formData.municipio}
                                        label="Município"
                                        onChange={handleChange as any}
                                        disabled={!formData.estado}
                                    >
                                        {municipiosDisponiveis.map((m) => (
                                            <MenuItem key={m} value={m}>
                                                {m}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" fullWidth>
                                    <InputLabel>Tipo de Construção</InputLabel>
                                    <Select
                                        name="tipo_de_construcao"
                                        value={formData.tipo_de_construcao}
                                        label="Tipo de Construção"
                                        onChange={handleChange as any}
                                    >
                                        <MenuItem value="apartamento">Apartamento</MenuItem>
                                        <MenuItem value="casa">Casa</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    size="small"
                                    name="orcamento_inicial"
                                    label="Orçamento Inicial da Obra"
                                    type="number"
                                    value={formData.orcamento_inicial ?? ''}
                                    onChange={handleChange}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }}
                                />
                            </Box>

                            <Divider textAlign="left">
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                    PRAZOS E CRONOGRAMA
                                </Typography>
                            </Divider>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                                    gap: 1.5,
                                }}
                            >
                                <TextField
                                    size="small"
                                    name="data_inicio"
                                    label="Data de Início"
                                    type="date"
                                    value={formData.data_inicio}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: '9999-12-31' }}
                                    required
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    name="data_termino_prevista"
                                    label="Data de Término Prevista"
                                    type="date"
                                    value={formData.data_termino_prevista}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: '9999-12-31' }}
                                    required
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    name="data_limite_correcao"
                                    label="Data Limite Correção Monetária"
                                    type="date"
                                    value={formData.data_limite_correcao}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: '9999-12-31' }}
                                    fullWidth
                                />
                            </Box>

                            <Divider textAlign="left">
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                    DOCUMENTAÇÃO LEGAL
                                </Typography>
                            </Divider>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' },
                                    gap: 1.5,
                                }}
                            >
                                <TextField
                                    size="small"
                                    name="cno"
                                    label="CNO"
                                    value={formData.cno}
                                    onChange={handleChange}
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    name="inscricao_municipal"
                                    label="Inscrição Municipal"
                                    value={formData.inscricao_municipal}
                                    onChange={handleChange}
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    name="matricula_imovel"
                                    label="Matrícula do Imóvel"
                                    value={formData.matricula_imovel}
                                    onChange={handleChange}
                                    fullWidth
                                />
                                <TextField
                                    size="small"
                                    name="numero_alvara"
                                    label="Nº do Alvará"
                                    value={formData.numero_alvara}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Box>
                        </Box>
                    )}

                    {/* ABA 1 E ABA 2: RESPONSÁVEIS OU UNIDADES */}
                    {(activeTab === 1 || activeTab === 2) && (
                        <ObraResponsaveisUnidadesSection
                            open={open}
                            mode={activeTab === 1 ? 'responsaveis' : 'unidades'}
                            tipoConstrucao={formData.tipo_de_construcao}
                            orcamentoInicial={Number(formData.orcamento_inicial) || 0}
                            quantidadeUnidadesManual={formData.quantidade_unidades ?? ''}
                            valorVendaUnidadeManual={formData.valor_venda_unidade ?? ''}
                            onManualFieldsChange={(qtd, valorMedio) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    quantidade_unidades: qtd as any,
                                    valor_venda_unidade: valorMedio as any,
                                }))
                            }
                            responsaveis={responsaveis}
                            onResponsaveisChange={setResponsaveis}
                            unidades={unidades}
                            onUnidadesChange={setUnidades}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancelar
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ObraCreateModal;
